import React, { useEffect, useState, SyntheticEvent } from 'react'
import { flowRight as compose } from 'lodash'
import { generatePath, useHistory, useParams } from 'react-router-dom'
import { captureException } from '@sentry/browser'

import { Pagination, Button, Table, BulletLabel } from 'components'
import { THead } from 'components/Table'
import { IconImport, IconPlus } from 'components/Icons'
import {
  ProductList,
  findAll,
  HEADERS,
  renderBreadcrumbsList,
  downloadProductTemplate,
  PRODUCTTEMPLATE,
} from 'features/Product'
import { BranchService, initialBranchDetail, Branch } from 'features/Branch'
import { TicketLabel } from 'features/Ticket'
import { withBackOfficeCms } from 'helpers/hocs'
import { routes } from 'helpers/routes'
import { onPageChange, getPhoneFormatter } from 'helpers/common'
import { LIMIT_PAGE } from 'helpers/constants'
import { useBreadcrumbs, useSearchParams, useCancelToken } from 'helpers/hooks'
import { useTranslation } from 'react-i18next'
import { CONTACT_TYPE } from 'types'

import type { ISorting, Product, IParamProduct } from 'types'
import type { IParamQuery } from 'libs/apis/helper'
import { OnboardingContainer } from 'containers/Onboarding'
import { downloadTemplate } from 'helpers/download'

const ProductContainer = (): JSX.Element => {
  const base = 'con-product'

  const history = useHistory()
  const { branchId } = useParams<{ branchId: string }>()
  const [totalPage, setTotalPages] = useState<number>(0)
  const [searchParams, setSearchParams] = useSearchParams<IParamQuery>()
  const [detailBranch, setDetailBranch] = useState<Branch>(initialBranchDetail)
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [isOpenOnboarding, setIsOpenOnboarding] = useState(false)
  const [template, setTemplateType] = useState<string>('')

  const { newCancelToken, isCancel } = useCancelToken()

  const getProductsInBranch = async (): Promise<void> => {
    setLoading(true)

    try {
      const params = { branchId, ...searchParams } as IParamProduct
      const response = await findAll(params, {
        cancelToken: newCancelToken(),
      })
      const { body, totals } = response

      setTotalPages(Math.ceil(Number(totals) / LIMIT_PAGE) || 1)
      setProducts(body)
      setLoading(false)
      setIsError(false)
    } catch (error) {
      if (isCancel(error)) return

      setLoading(false)
      setIsError(true)
      captureException(error)
    }
  }

  const phoneNumber = detailBranch?.contacts?.find(
    (contact) => contact.type === CONTACT_TYPE.PHONE,
  )

  const phone = phoneNumber?.number
    ? getPhoneFormatter(phoneNumber?.number as string)
    : '-'

  const { t } = useTranslation(['layout', 'product', 'company', 'branch'])

  const _page = Number(searchParams._page) || 1

  useBreadcrumbs(renderBreadcrumbsList(branchId))

  useEffect(() => {
    const getDetailBranch = async () => {
      try {
        const responseDetailBranch = await BranchService.findBranchById(
          branchId,
        )
        setDetailBranch(responseDetailBranch.data || initialBranchDetail)
      } catch (error) {
        captureException(error)
      }
    }

    getDetailBranch()
  }, [])

  useEffect(() => {
    getProductsInBranch()
  }, [searchParams._page, searchParams._sort, searchParams._order])

  const handleClickRow =
    (branchId: string, productId: string) => (e: SyntheticEvent) => {
      e.preventDefault()

      const path = generatePath(routes.branch.product.detail.path, {
        branchId,
        productId,
      })

      history.push(path)
    }

  const handleSortChange = (sort: ISorting): void =>
    setSearchParams({ _sort: sort.column, _order: sort.order, _page: 1 })

  const getExcelTemplate = async () => {
    try {
      const template = await downloadProductTemplate()
      downloadTemplate(template, PRODUCTTEMPLATE)
    } catch (e) {
      if (isCancel(e)) return
    }
  }
  return (
    <div className={base}>
      <div className={`${base}__text-header-branch`}>
        {t('product:branchDetail.title')}
      </div>
      <div className={`${base}__container-detail-branch`}>
        <div className={`${base}__max-width-detail-branch`}>
          <TicketLabel
            isLoading={loading}
            skeletonWidthValue={'100px'}
            title='product:branchId.title'
            value={detailBranch.code}
            isShowSkeletonTitle
          />
        </div>
        <div className={`${base}__max-width-detail-branch`}>
          <TicketLabel
            isLoading={loading}
            skeletonWidthValue={'200px'}
            title='company:name.label'
            value={detailBranch.companyName}
            isShowSkeletonTitle
          />
        </div>
        <div className={`${base}__max-width-detail-branch`}>
          <TicketLabel
            isLoading={loading}
            skeletonWidthValue={'200px'}
            title='product:branchName.title'
            value={detailBranch.name}
            isShowSkeletonTitle
          />
        </div>

        <div className={`${base}__max-width-detail-branch`}>
          <TicketLabel
            isLoading={loading}
            skeletonWidthValue={'100px'}
            title='branch:phone.label'
            value={phone}
            isShowSkeletonTitle
          />
        </div>
      </div>
      <div className={`${base}__container-product-status`}>
        <div className={`${base}__text-header-branch`}>
          {t('product:productBranchDetail.title')}
        </div>
        <div className={`${base}__float-button--wrap`}>
          <div className={`${base}__float-button--group`}>
            <Button
              onClick={() => {
                setTemplateType('product')
                setIsOpenOnboarding(true)
              }}
              variants='outline'
              withIcon={<IconImport variants='sm' />}
            >
              {t('branch:import.sku.button')}
            </Button>
            <Button
              withIcon={<IconPlus variants='sm' />}
              onClick={() => history.push(`/branch/${branchId}/product/new`)}
            >
              {t('product:create.button')}
            </Button>
          </div>
        </div>
      </div>

      <div className='list-content'>
        <div className='content-bullet'>
          <BulletLabel isActive isShowLabel />
          <BulletLabel isShowLabel />
        </div>
        <div className='row'>
          <Table>
            <THead
              columns={HEADERS}
              onSortChange={handleSortChange}
              position='left'
              sorting={{
                column: searchParams._sort as string,
                order: searchParams._order || 'ASC',
              }}
            />
            <ProductList
              columns={HEADERS}
              products={products}
              loading={loading}
              isError={isError}
              branchId={branchId}
              currentPage={_page}
              onClickRow={handleClickRow}
            />
          </Table>
        </div>
      </div>

      <div className={`${base}__con-bottom`}>
        <Pagination
          currentPage={_page}
          totalPages={totalPage}
          onChange={(page) =>
            onPageChange(() => setSearchParams({ _page: page }))
          }
          disabled={loading}
        />
      </div>

      <OnboardingContainer
        isOpen={isOpenOnboarding}
        onClose={() => setIsOpenOnboarding(false)}
        type={template}
        onDownload={() => getExcelTemplate()}
      />
    </div>
  )
}

export default compose(withBackOfficeCms)(ProductContainer)
